<template>
    <v-container fluid>
        <div>
            <slot name="breadcrumbs" />
        </div>

        <v-form @submit.prevent="filter()">
            <v-row dense class="my-0">
                <!-- account -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-autocomplete
                        v-model="filterData.accountId"
                        :label="$t('inputs.account')"
                        :items="accounts.filter(c => c.type === type.sub) || []"
                        :item-text="c => `${c.accounting_no} - ${c.name}`"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                    />
                </v-col>
                
                <!-- material -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-autocomplete
                        v-model="filterData.materialId"
                        :label="$t('inputs.material')"
                        :items="materials.filter(c => c.type === type.sub) || []"
                        :item-text="c => `${c.no} - ${c.name}`"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                    />
                </v-col>
                
                <!-- from-date -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <bee-date-input
                        v-model="filterData.fromDate"
                        :label="$t('inputs.from-date')"
                        dense
                        hide-details
                        prepend-icon="mdi-calendar"
                        user-menu-picker
                        input-format-order="DD-MM-YYYY"
                    ></bee-date-input>
                </v-col>
                
                <!-- to-date -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <bee-date-input
                        v-model="filterData.toDate"
                        :label="$t('inputs.to-date')"
                        dense
                        hide-details
                        prepend-icon="mdi-calendar"
                        user-menu-picker
                        input-format-order="DD-MM-YYYY"
                    ></bee-date-input>
                </v-col>

                <v-col cols="auto">
                    <v-btn dark color="info-light-color" type="submit">
                        <v-icon class="me-1">mdi-magnify</v-icon>
                        {{$t('search')}}
                    </v-btn>
                </v-col>
                <v-spacer/>
                <v-col cols="auto">
                    <v-menu offset-y>
                        <template v-slot:activator="{ attr, on }">
                            <v-btn dark color="alert-color" v-on="on" v-bind="attr">
                                <v-icon class="me-1">mdi-export-variant</v-icon>
                                {{$t('exports.export')}}
                            </v-btn>
                        </template>

                        <v-list nav dense>
                            <v-list-item @click="getReport('pdf')">
                                <v-list-item-icon class="me-4">
                                    <v-icon>mdi-printer</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{$t('exports.export-as-pdf')}}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="getReport('excel')">
                                <v-list-item-icon class="me-4">
                                    <v-icon>mdi-microsoft-excel</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{$t('exports.export-as-excel')}}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>
            </v-row>
        </v-form>

        <!-- table -->
        <bee-handy-table
        	:headers="headers"
            :items="loading ? [] : salesStatement"
            fixed-header
            :items-per-page="25"
            pagination-on-scroll
            :height="$vuetify.breakpoint.height - 214"
            :data-table-props="{ disableSort: true }"
            :loading="loading"
            :shown-rows-text="$t('shown-rows-text')"
            :context-menu-items="contextMenuItems"
            @contextmenu:row="(e, { item }) => relatedId = item.materials_journal_id"
            @context-menu-show-related-journal="
                $router.push({ name: 'materials-journal', params: { id: relatedId } })
            "
        >
            <template v-slot:item.total="{ item }">
                {{(item.price.amount * item.quantity.quantity).toLocaleString()}}
            </template>
        </bee-handy-table>
    </v-container>
</template>

<script>
import { useSalesStatement } from '@/configs/global'
import { mapGetters, mapState } from 'vuex';
import { transactionType, type } from '@/helpers/enums'
import ls from "@/settings/localStorageExtension";
import moment from 'moment';

export default {
    name: 'SalesStatement',

    data() {
        return {
            // helpers
            type,
            loading: false,
            relatedId: null,

            filterData: {
                accountId: null,
                materialId: null,
                fromDate: moment().subtract('months', 3).format('YYYY-MM-DD'),
                toDate: moment().format('YYYY-MM-DD'),
            }
        }
    },

    computed: {
        ...mapState({
            currentFinancialFile: state => state.financialFiles.current_financial_file,
            accounts: state => state.configs.configs.accounts,
            materials: state => state.materials.materials,
            salesStatement: state => state.statements.salesStatement,
        }),
        
        ...mapGetters({
            getAccountById: 'configs/getAccountById',
            getMaterialById: 'materials/getMaterialById',
        }),

        headers() {
            const headers = [
                {
                    text: this.$t('headers.material'),
                    name: 'material',
                    value: 'material_id',
                    setValueExpr: val => this.getMaterialById(val)?.name
                },
                {
                    text: this.$t('headers.quantity'),
                    name: 'quantity',
                    value: 'quantity.quantity',
                    setValueExpr: val => val.toLocaleString(),
                },
                {
                    text: this.$t('headers.price'),
                    name: 'price',
                    value: 'price.amount',
                    setValueExpr: val => val.toLocaleString(),
                },
                {
                    text: this.$t('headers.total'),
                    name: 'total',
                    value: 'total',
                },
                {
                    text: this.$t('headers.account'),
                    name: 'account',
                    value: 'client_id',
                    setValueExpr: val => this.getAccountById(val)?.name
                },
                {
                    text: this.$t('headers.ref-doc'),
                    name: 'refDoc',
                    value: 'doc_no',
                },
                {
                    text: this.$t('headers.date'),
                    name: 'date',
                    value: 'date', 
                    type: 'date', 
                },
            ];
            for (let i = 0; i < headers.length; i++) {
                headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
            }
            return headers
        },
        
        contextMenuItems() {
            return [
                {
                    title: this.$t('menus.show-related-journal'),
                    event: 'show-related-journal'
                },
            ]
        },
    },

    methods: {
        filter(isFirstLoad) {
            this.loading = true;
            this.$store.dispatch('statements/fetchSalesStatements', {
                materialId: this.filterData.materialId,
                clientId: this.filterData.accountId,
                fromDate: this.filterData.fromDate,
                toDate: this.filterData.toDate,
                transactionType: transactionType.out // out
            }).finally(() => {
                if (!isFirstLoad) this.loading = false;
            })
        },

        getReport(type) {
            this.$store.dispatch('reports/salesStatementReport', {
                type,
                financialFileId: this.currentFinancialFile.id,
                userId: ls.getLocalUser().id,
                materialId: this.filterData.materialId,
                accountId: this.filterData.accountId,
                fromDate: this.filterData.fromDate,
                toDate: this.filterData.toDate,
            })
        }
    },

    created() {
        this.loading = true;
        Promise.all([
            !this.materials.length ? this.$store.dispatch('materials/fetchAll') : null,
            this.filter(),
        ]).finally(() => {
            this.loading = false;
        });
    },

    beforeRouteEnter(to, from, next) {
        if (useSalesStatement) next(true);
        else next({ name: 'home' });
    }
}
</script>

<style>

</style>